<template>
    <common-content-spacer height="medium" class="reservation-list">
        <common-table>
            <thead>
                <tr>
                    <th>{{ typeTitle() }}</th>
                    <th>{{ $t('components.reservations.reservation_list.table.titles.offer') }}</th>
                    <th>{{ $t('components.reservations.reservation_list.table.titles.location') }}</th>
                    <th>{{ $t('components.reservations.reservation_list.table.titles.created_date') }}</th>
                    <th>{{ $t('components.reservations.reservation_list.table.titles.scheduled_for') }}</th>
                    <th>{{ $t('components.reservations.reservation_list.table.titles.status') }}</th>
                    <th></th>
                </tr>
            </thead>

            <tbody>
                <tr v-for="reservation in reservations" :key="reservation.id">
                    <td :data-label="typeTitle()">
                        <div class="reservation-list__user">
                            <common-user-avatar
                                v-if="reservation.initiator"
                                :user="reservation.initiator"
                                :shouldLeadToProfile="true"
                                size="medium-small"
                            ></common-user-avatar>
                            <common-company-avatar
                                v-else-if="reservation.availabilityOffer.linkedOffer.location.companyServiceProvider"
                                :company="reservation.availabilityOffer.linkedOffer.location.companyServiceProvider"
                                :shouldLeadToProfile="true"
                                size="medium-small"
                            ></common-company-avatar>
                            <common-user-avatar
                                v-else
                                :user="reservation.availabilityOffer.linkedOffer.location.userServiceProvider"
                                :shouldLeadToProfile="true"
                                size="medium-small"
                            ></common-user-avatar>
                            <span class="reservation-list__user--name" @click="nameClicked(reservation)">
                                {{ getDisplayName(reservation) }}
                            </span>
                            <a
                                v-if="isReservationsReceived && !isTabletSmall"
                                class="reservation-list__user--phone"
                                :href="`tel:${reservation?.initiator?.mobile}`"
                            >
                                <common-icon name="phone" size="medium"></common-icon>
                            </a>
                        </div>
                    </td>
                    <td v-if="isReservationsReceived && isTabletSmall" :data-label="$t('common.microcopy.phone')">
                        <a
                            v-if="reservation.phoneShow"
                            class="single-reservation__phone--text"
                            :href="`tel:${reservation?.initiator?.mobile}`"
                        >
                            {{ reservation?.initiator?.mobile }}
                        </a>
                        <a v-else class="single-reservation__phone--text" @click="reservation.phoneShow = true">
                            {{ $t('components.user.user_card.click_phone') }}
                        </a>
                    </td>
                    <td
                        :data-label="$t('components.reservations.reservation_list.table.titles.offer')"
                        class="reservation-list__offer"
                    >
                        {{ getTranslation(reservation?.availabilityOffer?.linkedOffer?.titleTextContent) }}
                    </td>
                    <td :data-label="$t('components.reservations.reservation_list.table.titles.location')">
                        {{ reservation.availabilityOffer.linkedOffer.location.address }}
                        <br />
                        {{ reservation.availabilityOffer.linkedOffer.location.city.name }}
                    </td>
                    <td :data-label="$t('components.reservations.reservation_list.table.titles.created_date')">
                        {{ getDateTime(reservation.createdAt) }}
                    </td>
                    <td :data-label="$t('components.reservations.reservation_list.table.titles.scheduled_for')">
                        {{ getDate(reservation.date) }}
                        <br />
                        {{
                            getTime(
                                reservation.startTime,
                                reservation.date,
                                reservation.availabilityOffer.linkedOffer.location.timeZone,
                            )
                        }}
                        -
                        {{
                            getTime(
                                reservation.allowOverlapAfterMinutes
                                    ? reservation.startTime + reservation.allowOverlapAfterMinutes
                                    : reservation.endTime,
                                reservation.date,
                                reservation.availabilityOffer.linkedOffer.location.timeZone,
                            )
                        }}
                    </td>
                    <td :data-label="$t('components.reservations.reservation_list.table.titles.status')">
                        <reservation-status :status="reservation.status"></reservation-status>
                    </td>
                    <td class="reservation-list__last-td">
                        <common-button
                            type="primary"
                            size="tiny"
                            :isFluid="true"
                            :label="getActionLabel(reservation.status)"
                            @click="goToReservation(reservation.id)"
                        ></common-button>
                    </td>
                </tr>
            </tbody>
        </common-table>

        <slot class="reservation-list__buttons"></slot>
    </common-content-spacer>
</template>

<script>
    import ReservationStatus from 'components/page-elements/reservations/ReservationStatus'
    import availabilityTransformer from 'transformers/availabilityTransformer'
    import dayjs from 'dayjs'
    import dayjsPluginUTC from 'dayjs/plugin/utc'
    import ReservationStatusEnum from 'enums/reservation_status.enum'
    import ReservationTypeEnum from 'enums/reservation_type.enum'
    import RoutesEnum from 'enums/routes.enum'
    import TranslateMixin from 'mixins/TranslateMixin'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'

    dayjs.extend(dayjsPluginUTC)

    export default {
        mixins: [TranslateMixin, WindowResizeMixin],

        components: { ReservationStatus },

        props: {
            reservations: { type: Array, required: true },
            isReservationsReceived: { type: Boolean, default: false },
        },

        methods: {
            getDate(date) {
                return dayjs.utc(date).format('DD.MM.YYYY')
            },
            getTime(time, date, timeZone) {
                return availabilityTransformer.minutesDateToLocalTZTime(time, date, timeZone)
            },
            getDateTime(date) {
                return dayjs(date).add(dayjs().utcOffset()).format('DD.MM.YYYY HH:mm')
            },
            getActionLabel(status) {
                if (this.$route.params.type === ReservationTypeEnum.MADE) {
                    return this.$t('common.microcopy.view')
                }
                return status === ReservationStatusEnum.OPEN
                    ? this.$t('common.microcopy.respond')
                    : this.$t('common.microcopy.view')
            },
            goToReservation(id) {
                this.$router.push({ name: RoutesEnum.RESERVATIONS.SINGLE, params: { id } })
            },
            typeTitle() {
                return this.$route.params.type === ReservationTypeEnum.RECEIVED
                    ? this.$t('components.reservations.reservation_list.table.titles.reserved_by')
                    : this.$t('components.reservations.reservation_list.table.titles.received')
            },
            getDisplayName(reservation) {
                if (reservation.initiator) {
                    return `${reservation.initiator.firstName} ${reservation.initiator.lastName}`
                } else if (reservation.availabilityOffer.linkedOffer.location.companyServiceProvider) {
                    return reservation.availabilityOffer.linkedOffer.location.companyServiceProvider.name
                } else {
                    const user = reservation.availabilityOffer.linkedOffer.location.userServiceProvider
                    return `${user.firstName} ${user.lastName}`
                }
            },
            nameClicked(reservation) {
                if (reservation.initiator) {
                    this.$router.push({ name: RoutesEnum.USER.PROFILE, params: { id: reservation.initiator.id, tab: 'profile' } })
                } else if (reservation.availabilityOffer.linkedOffer.location.companyServiceProvider) {
                    this.$router.push({
                        name: RoutesEnum.COMPANY.PROFILE,
                        params: {
                            id: reservation.availabilityOffer.linkedOffer.location.companyServiceProvider.name.id,
                            companyType: 'profile',
                        },
                    })
                } else {
                    this.$router.push({
                        name: RoutesEnum.USER.PROFILE,
                        params: { id: reservation.availabilityOffer.linkedOffer.location.userServiceProvider.id, tab: 'profile' },
                    })
                }
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/reservations/_reservation-list.scss';
</style>
